import React from "react";
import cx from "classnames";

import { Loading } from "@common/components/loading";
import { Field } from "./components/field";
import { FieldPickerField, FieldsProps } from "./fields.types";
import { trDts } from "@modules/datasheets/utils/i18n";

import "./fields.styles.scss";

export const Fields: React.FC<FieldsProps> = (props) => {
  const { fields, loading, className, selection, style, dndType, isFieldAdded, onFieldAlreadyAdded } = props;
  const lastSelectedFieldIdRef = React.useRef<string>();

  const isMultiSelectModifierPressed = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    return event.ctrlKey || event.metaKey || event.shiftKey;
  }, []);

  const mergeUniqueFields = React.useCallback((items: Array<FieldPickerField>) => {
    return items.reduce<Array<FieldPickerField>>((acc, currentItem) => {
      if (!acc.some((item) => item.id === currentItem.id)) {
        acc.push(currentItem);
      }

      return acc;
    }, []);
  }, []);

  const handleFieldClickUp = React.useCallback(({ item, event }: { item: FieldPickerField; event: React.MouseEvent<HTMLDivElement> }) => {
    if (isFieldAdded(item)) {
      onFieldAlreadyAdded?.(item);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const alreadySelectedItems = selection.items;

    if (!isMultiSelectModifierPressed(event) && alreadySelectedItems.length > 1) {
      selection.onChange([item]);
      lastSelectedFieldIdRef.current = item.id;
    }
  },
  [selection, isFieldAdded, onFieldAlreadyAdded, isMultiSelectModifierPressed]);

  const handleFieldClickDown = React.useCallback(({ item, event }: { item: FieldPickerField; event: React.MouseEvent<HTMLDivElement> }) => {
    if (isFieldAdded(item)) {
      onFieldAlreadyAdded?.(item);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const alreadySelectedItems = selection.items;

    if (!isMultiSelectModifierPressed(event) && alreadySelectedItems.length > 1) {
      return;
    }

    const isAlreadySelected = alreadySelectedItems.some((x) => x.id === item.id);

    if (!isMultiSelectModifierPressed(event)) {
      if (alreadySelectedItems.length === 1 && isAlreadySelected) {
        lastSelectedFieldIdRef.current = item.id;
        return;
      }

      selection.onChange([item]);
      lastSelectedFieldIdRef.current = item.id;
      return;
    }

    if (event.shiftKey) {
      const anchorId =
        lastSelectedFieldIdRef.current ??
        alreadySelectedItems[alreadySelectedItems.length - 1]?.id ??
        item.id;
      const anchorIndex = fields.findIndex((field) => field.id === anchorId);
      const currentIndex = fields.findIndex((field) => field.id === item.id);

      if (anchorIndex === -1 || currentIndex === -1) {
        const fallbackSelection = isAlreadySelected ? alreadySelectedItems : alreadySelectedItems.concat(item);
        selection.onChange(fallbackSelection);
        lastSelectedFieldIdRef.current = item.id;
        return;
      }

      const [start, end] = anchorIndex < currentIndex
        ? [anchorIndex, currentIndex]
        : [currentIndex, anchorIndex];
      const rangeItems = fields.slice(start, end + 1);
      selection.onChange(mergeUniqueFields([...alreadySelectedItems, ...rangeItems]));
      lastSelectedFieldIdRef.current = item.id;
      return;
    }

    const newSelectedItems = isAlreadySelected
      ? alreadySelectedItems.filter((x) => x.id !== item.id)
      : alreadySelectedItems.concat(item);

    selection.onChange(newSelectedItems);
    lastSelectedFieldIdRef.current = item.id;
  }, [selection, isFieldAdded, onFieldAlreadyAdded, isMultiSelectModifierPressed, fields, mergeUniqueFields]);

  const isFieldSelected = React.useCallback((item: FieldPickerField) => {
    return selection.items.some((x) => x.id === item.id) ?? false;
  }, [selection.items]);

  return (
    <div className={cx("DatasheetsEditorFields", className)} style={style}>
      {loading && <Loading size={20} margin="20px" />}
      {!loading && !fields.length && (
        <div className="DatasheetsEditorFields__no-data-message">{trDts("Please select the folder or search fields")}</div>
      )}
      {!loading && fields.length > 0 && (
        <div className="DatasheetsEditorFields__list">
          {fields.map((field) => (
            <Field
              key={field.id}
              dndType={dndType}
              field={field}
              isAdded={isFieldAdded(field)}
              onMouseUp={handleFieldClickUp}
              onMouseDown={handleFieldClickDown}
              isSelected={isFieldSelected(field)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
